import {
  post, get, put, destroy,
} from '../utils/request';
import { resetActiveSubmission } from './submission';
import { setCatalogItems } from './catalogItems';
import { setProviderList } from './providerList';
import { setCompanyLogoUrl } from './companyLogo';
import { redirectUrl } from '../utils/configuration';
import { resetStoreProducts } from './storeProducts';

export const signUp = (params) => async () => {
  try {
    const response = await post('/auth', params);

    const body = await response.json();

    // Le statut voyage avec le corps, comme pour `handleSendEmailResetPassword` : un 500
    // n'a pas de clé `errors`, et l'écran d'inscription ne savait alors rien dire.
    return { ...body, httpStatus: response.status };
  } catch (error) {
    return false;
  }
};

export const signIn = (username, password) => async (dispatch) => {
  try {
    const params = {
      email: username,
      password,
    };

    const response = await post('/auth/sign_in', params, dispatch);
    const body = await response.json();

    const { data } = body;

    const {
      catalogItems, providers, companyLogo,
    } = data;

    const headers = {
      accessToken: response.headers.get('access-token'),
      client: response.headers.get('client'),
      uid: data.uid,
    };

    const { errors } = body;
    if (errors) {
      return false;
    }

    // reset
    dispatch(removeUser());
    dispatch(resetActiveSubmission());
    dispatch(resetStoreProducts());

    dispatch(setUser(data));
    dispatch(setProviderList(providers));
    dispatch(setAuthInfo(headers));
    dispatch(setCatalogItems(catalogItems));
    dispatch(setCompanyLogoUrl(companyLogo));

    return true;
  } catch (error) {
    return false;
  }
};

export const signOut = (user) => async (dispatch) => {
  try {
    const response = await destroy('/auth/sign_out', dispatch, credentials(user));
    const body = await response.json();

    const { success } = body;

    if (success) {
      dispatch(removeUser());
      dispatch(resetActiveSubmission());
      dispatch(resetStoreProducts());
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const validateUserToken = (user) => async (dispatch) => {
  try {
    const response = await get('/auth/validate_token', dispatch, credentials(user));
    const body = await response.json();
    const { success, data } = body;

    if (success) {
      dispatch(setUser(data));
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
};

// Les 4 actions « type de travail » renvoient TOUJOURS les deux listes (cf. le `success` du
// WorkTypeController), parce qu'un type de travail et ses CELLULES sont indissociables :
//   - créer un type SÈME ses 4 cellules (after_create) — sans elles, le nouveau métier
//     s'affichait vide et il n'y avait « rien à faire » avec (bug signalé par le développeur) ;
//   - le supprimer détruit les siennes (sinon elles restaient, fantômes, dans le magasin) ;
//   - le RENOMMER change le nom composé de ses cellules (« Plomberie · Matériaux »), qui est
//     dérivé côté API — les garder eût affiché l'ancien nom partout.
// Il faut donc dispatcher les DEUX à chaque fois. Ne prendre que `workTypes` était le bug.
export const handleCreateNewWorkType = (params, user) => async (dispatch) => {
  try {
    const response = await post('/work_type', params, dispatch, credentials(user));

    const body = await response.json();

    const { workTypes, costCategories } = body;

    if (workTypes) dispatch(setUserWorkTypes(workTypes));
    if (costCategories) dispatch(setUserCostCategories(costCategories));

    return workTypes || true;
  } catch (error) {
    return false;
  }
};

export const handleDeleteWorkType = (params, user) => async (dispatch) => {
  try {
    const response = await post('/delete_work_type', params, dispatch, credentials(user));

    const body = await response.json();

    const { workTypes, costCategories } = body;

    if (workTypes) dispatch(setUserWorkTypes(workTypes));
    if (costCategories) dispatch(setUserCostCategories(costCategories));

    return true;
  } catch (error) {
    return false;
  }
};

export const handleUpdateWorkType = (id, params, user) => async (dispatch) => {
  try {
    const response = await put(`/work_type/${id}`, params, dispatch, credentials(user));

    const body = await response.json();

    const { workTypes, costCategories } = body;

    if (workTypes) dispatch(setUserWorkTypes(workTypes));
    if (costCategories) dispatch(setUserCostCategories(costCategories));

    return true;
  } catch (error) {
    return false;
  }
};

export const handleSendEmailResetPassword = (email) => async (dispatch) => {
  try {
    const params = {
      email,
      redirect_url: redirectUrl,
    };
    const response = await post('/auth/password', params, dispatch);
    const body = await response.json();

    // Le statut voyage avec le corps. Sans lui, l'écran ne peut pas distinguer un refus
    // métier — 404 « adresse inconnue », dont le corps porte `errors` — d'une panne (500)
    // ou d'un trop-plein de tentatives (429), dont les corps n'ont PAS cette clé et
    // arrivaient donc dans le snackbar comme un message vide.
    return { ...body, httpStatus: response.status };
  } catch (error) {
    return false;
  }
};

export const handleResetPasswordWithEmail = (uid, client, accessToken, newPassword, confirmNewPassword) => async (dispatch) => {
  try {
    const params = {
      password: newPassword,
      password_confirmation: confirmNewPassword,
    };

    const header = {
      'access-token': accessToken,
      client,
      uid,
    };

    const response = await put('/auth/password', params, dispatch, header);
    const body = await response.json();

    return body;
  } catch (error) {
    return false;
  }
};

export const handleUserChangePassword = (params, user) => async (dispatch) => {
  try {
    const response = await put('/auth/password', params, dispatch, credentials(user));
    const body = await response.json();

    return body;
  } catch (error) {
    return false;
  }
};

export const handleUpdateCompany = (params, user) => async (dispatch) => {
  try {
    const response = await post('/update_user', params, dispatch, credentials(user));
    const body = await response.json();

    // `fetch` ne lève PAS sur un 4xx : sans ce test, un refus du serveur (403 sur
    // un membre d'équipe) repartait en `true` et l'écran affichait « Enregistré »
    // alors que rien n'avait été écrit.
    if (!response.ok) return false;

    const { data } = body;
    if (data) dispatch(setUser(data));

    return true;
  } catch (error) {
    return false;
  }
};

// Retourne { ok, error } et non un booléen nu : le serveur nomme le champ fautif
// (« Téléphone n'est pas un numéro valide »), et l'écran vaut mieux que « Une erreur est
// survenue ».
//
// `fetch` ne lève PAS sur un 4xx : sans le test sur `response.ok`, un refus du serveur
// repartait en `true` et l'écran annonçait « Enregistré avec succès » sur une valeur
// jamais écrite. Même piège que `handleUpdateCompany` plus haut.
export const handleUpdateProfile = (params, user) => async (dispatch) => {
  try {
    const response = await post('/update_profile', params, dispatch, credentials(user));
    const body = await response.json();

    if (!response.ok) {
      return { ok: false, error: (body.errors || []).join(', ') || 'Une erreur est survenue' };
    }

    const { data } = body;
    if (data) dispatch(setUser(data));

    return { ok: true };
  } catch (error) {
    return { ok: false, error: 'Une erreur est survenue' };
  }
};

export const handleCreateSuggestion = (params, user) => async (dispatch) => {
  try {
    await post('/suggestion', params, dispatch, credentials(user));

    return true;
  } catch (error) {
    return false;
  }
};

// Retourne { ok, error } plutôt qu'un booléen nu : le tiroir de frais intégrés doit
// afficher le message de validation du serveur (formule invalide, drapeau éteint…)
// sans se fermer — un booléen seul ne porte pas ce message.
export const handleUpdateCostCategory = (id, params, user) => async (dispatch) => {
  try {
    const response = await put(`/cost_category/${id}`, params, dispatch, credentials(user));

    const body = await response.json();

    if (!response.ok) return { ok: false, error: body.error || 'Une erreur est survenue' };

    const { costCategories } = body;

    if (costCategories) dispatch(setUserCostCategories(costCategories));

    return { ok: true };
  } catch (error) {
    return { ok: false, error: 'Une erreur est survenue' };
  }
};

export const handleReorderWorkTypes = (ids, user) => async (dispatch) => {
  try {
    const response = await post('/reorder_work_types', { ids }, dispatch, credentials(user));

    const body = await response.json();

    const { workTypes, costCategories } = body;

    if (workTypes) dispatch(setUserWorkTypes(workTypes));
    if (costCategories) dispatch(setUserCostCategories(costCategories));

    return true;
  } catch (error) {
    return false;
  }
};

export const handleDeleteProductGroup = (id, user) => async (dispatch) => {
  try {
    const response = await destroy(`/product_group/${id}`, dispatch, credentials(user));

    const body = await response.json();

    const { productGroups } = body;

    if (productGroups) dispatch(setUserProductGroups(productGroups));

    return true;
  } catch (error) {
    return false;
  }
};

export const handleCreateNewProductGroup = (params, user) => async (dispatch) => {
  try {
    const response = await post('/product_group', params, dispatch, credentials(user));

    const body = await response.json();

    const { productGroups } = body;

    if (productGroups) dispatch(setUserProductGroups(productGroups));

    return productGroups || true;
  } catch (error) {
    return false;
  }
};

export const handleUpdateProductGroup = (id, params, user) => async (dispatch) => {
  try {
    const response = await put(`/product_group/${id}`, params, dispatch, credentials(user));

    const body = await response.json();

    const { productGroups } = body;

    if (productGroups) dispatch(setUserProductGroups(productGroups));

    return true;
  } catch (error) {
    return false;
  }
};

export const credentials = (user) => ({
  'access-token': user.accessToken,
  client: user.client,
  uid: user.uid,
});

// CE QUE L'ENTREPRENEUR A DÉJÀ FAIT DANS SA MISE EN ROUTE — masquer la carte d'accueil,
// avoir vu la présentation, avoir relu ses majorations.
//
// Ça vivait en mémoire locale : vider les données du navigateur remettait le compte à son
// premier jour (cf. firstStepsVisibility). L'écran nomme l'étape, le serveur sait si elle
// appartient à la personne ou à l'entreprise et renvoie les deux jeux à jour — qu'on remet
// dans le magasin pour que la carte se redessine sans recharger.
//
// Silencieux en cas d'échec, et c'est délibéré : personne n'a demandé à « enregistrer » quoi
// que ce soit, on a ouvert un panneau. Une alerte rouge sur un geste que l'entrepreneur n'a
// pas fait exprès ne lui apprendrait rien — au pire l'étape se recochera au prochain
// passage, ce qui est le comportement d'avant.
export const handleRecordOnboardingFlags = (flags, user) => async (dispatch) => {
  if (!flags || !flags.length) return { ok: false };
  try {
    const response = await post('/onboarding_flags', { flags }, dispatch, credentials(user));
    if (!response.ok) return { ok: false };

    const { data } = await response.json();
    if (data) dispatch(setUser(data));

    return { ok: true };
  } catch (error) {
    return { ok: false };
  }
};

// « Revoir les premiers pas » (bouton Aide) : le masquage est LEVÉ, pas contourné. Sans cet
// appel, la carte reviendrait le temps de la session et repartirait au rechargement — chez
// ceux-là mêmes qui viennent de la redemander.
export const handleClearOnboardingFlag = (flag, user) => async (dispatch) => {
  try {
    const response = await destroy(`/onboarding_flags/${flag}`, dispatch, credentials(user));
    if (!response.ok) return { ok: false };

    const { data } = await response.json();
    if (data) dispatch(setUser(data));

    return { ok: true };
  } catch (error) {
    return { ok: false };
  }
};

export const setUserWorkTypes = (workTypes) => ({
  type: 'SET_USER_CATEGORIES',
  workTypes,
});

export const setUserCostCategories = (costCategories) => ({
  type: 'SET_USER_SUB_CATEGORIES',
  costCategories,
});

export const setUserProductGroups = (productGroups) => ({
  type: 'SET_USER_PRODUCT_GROUPS',
  productGroups,
});

export const setUser = (user) => ({
  type: 'SET_USER',
  user,
});

const removeUser = () => ({
  type: 'REMOVE_USER',
});

const setAuthInfo = (headers) => ({
  type: 'SET_AUTH_INFOS',
  accessToken: headers.accessToken,
  client: headers.client,
  uid: headers.uid,
});
